import type { TranslationKey } from '@/i18n/translation-key.tsx';
import {
  PRODUCT_KEYS,
  PRODUCT_SCOPE_TYPES,
  type ProductDto,
  type ProductKey,
} from '@/types/dtos/orders/response/product.dto.ts';

/**
 * Per-SKU quota avatar behavior, set on the matching `EVENT_CATALOG_ENTRIES` row (`quotaAvatar`).
 * Use `balanceQuotaKey` when the avatar should reflect another balance row (e.g. a quota included
 * in a PACKAGE: same string as `packageConfig.includes.quotas[].quotaKey`).
 */
export type QuotaAvatarConfig = {
  /** When explicitly false, the quota avatar is not rendered. Defaults to true. */
  visible?: boolean;
  /**
   * Balance row `quotaKey` for used/total and status colors. When omitted, resolution
   * falls back to `getQuotaKeyForProduct` (`quotaConfig.quotaKey` or QUOTA catalog fallback).
   */
  balanceQuotaKey?: string;
  /** Overrides `quotaUsedTooltipKey` for this avatar (same `{ used, total }` interpolation). */
  tooltipKey?: TranslationKey;
};

export type EventCatalogEntry = {
  key: ProductKey;
  titleKey: TranslationKey;
  subtitleKey: TranslationKey;
  /** Long-form bullets: use ` - ` separators in the locale string, or a single segment. */
  detailsKey: TranslationKey;
  quotaUsedTooltipKey: TranslationKey;
  /** Default `quota_balances.quotaKey` when API omits `quotaConfig.quotaKey` */
  fallbackQuotaKey: string;
  /** Checkout: use contributor label when `snapshotUnit === 'count'` */
  snapshotCountUsesContributorUnitLabel: boolean;
  /**
   * When explicitly false, the SKU is omitted from event catalog lists (modal, dashboard features,
   * event list product strip) and excluded from related counts. Defaults to true.
   */
  visible?: boolean;
  /** Optional overrides for `EventCatalogProductQuotas` visibility and which balance row drives the avatar. */
  quotaAvatar?: QuotaAvatarConfig;
  /**
   * When set, the SKU is only listed in the event catalog if `GET /features/{name}/check-access` returns true.
   * Use the same kebab-case name as other `useFeatureAccess` checks.
   */
  displayFeatureFlag?: string;
  /**
   * When true, the line item is sent with `autoAssignToOrderScope: true` so that all purchased
   * PACKAGE_UNIT entitlements are automatically assigned to the order's event scope after payment.
   * Only valid for PACKAGE products whose `packageConfig.assignableTo` includes "Event".
   */
  autoAssignToOrderScope?: boolean;
};

export const EVENT_CATALOG_ENTRIES = [
  {
    key: PRODUCT_KEYS.EVENT_CONTRIBUTOR,
    titleKey: 'event.features.contributor.title',
    subtitleKey: 'event.features.contributor.subtitle',
    detailsKey: 'event.features.contributor.details',
    quotaUsedTooltipKey: 'event.features.contributorsUsed',
    fallbackQuotaKey: 'contributors',
    snapshotCountUsesContributorUnitLabel: true,
    autoAssignToOrderScope: true,
    quotaAvatar: {
      balanceQuotaKey: 'contributors',
      tooltipKey: 'event.features.contributorsUsed',
    },
  },
  {
    key: PRODUCT_KEYS.EVENT_REMOTE_SPEAKER_DAY,
    titleKey: 'event.features.remoteSpeakerDay.title',
    subtitleKey: 'event.features.remoteSpeakerDay.subtitle',
    detailsKey: 'event.features.remoteSpeakerDay.details',
    quotaUsedTooltipKey: 'event.features.remoteSpeakerDaysUsed',
    fallbackQuotaKey: 'remote_speaker_days',
    snapshotCountUsesContributorUnitLabel: false,
    visible: false,
    quotaAvatar: {
      balanceQuotaKey: 'remote_speaker_days',
      tooltipKey: 'event.features.remoteSpeakerDaysUsed',
    },
  },
  {
    key: PRODUCT_KEYS.DATA_TRANSFER_GB,
    titleKey: 'event.features.mediaStorage.title',
    subtitleKey: 'event.features.mediaStorage.subtitle',
    detailsKey: 'event.features.mediaStorage.details',
    quotaUsedTooltipKey: 'event.features.mediaStorageUsed',
    fallbackQuotaKey: 'data_transfer_gb',
    snapshotCountUsesContributorUnitLabel: false,
  },
  {
    key: PRODUCT_KEYS.CONTRIBUTOR,
    titleKey: 'event.features.contributor.title',
    subtitleKey: 'event.features.contributor.subtitle',
    detailsKey: 'event.features.contributor.details',
    quotaUsedTooltipKey: 'event.features.contributorsUsed',
    fallbackQuotaKey: 'contributors',
    snapshotCountUsesContributorUnitLabel: true,
    visible: false,
  },
  {
    key: PRODUCT_KEYS.PLAY_RUN,
    titleKey: 'event.features.playRun.title',
    subtitleKey: 'event.features.playRun.subtitle',
    detailsKey: 'event.features.playRun.details',
    quotaUsedTooltipKey: 'event.features.playRunsUsed',
    fallbackQuotaKey: 'play_runs',
    snapshotCountUsesContributorUnitLabel: false,
    displayFeatureFlag: 'perform',
  },
  {
    key: PRODUCT_KEYS.VM_USE,
    titleKey: 'event.features.vmUse.title',
    subtitleKey: 'event.features.vmUse.subtitle',
    detailsKey: 'event.features.vmUse.details',
    quotaUsedTooltipKey: 'event.features.vmUsesUsed',
    fallbackQuotaKey: 'vm_uses',
    snapshotCountUsesContributorUnitLabel: false,
    displayFeatureFlag: 'broadcast',
  },
] as const satisfies readonly EventCatalogEntry[];

export type EventCatalogProductKey = (typeof EVENT_CATALOG_ENTRIES)[number]['key'];

export const EVENT_PRODUCT_KEYS: readonly EventCatalogProductKey[] = EVENT_CATALOG_ENTRIES.map((e) => e.key);

const PRODUCT_CATALOG_ENTRY_BY_KEY = EVENT_CATALOG_ENTRIES.reduce(
  (acc, entry) => {
    acc[entry.key] = entry;
    return acc;
  },
  {} as Record<EventCatalogProductKey, EventCatalogEntry>,
);

export function getProductCatalogEntry(key: string | undefined): EventCatalogEntry | undefined {
  if (!key) return undefined;
  return PRODUCT_CATALOG_ENTRY_BY_KEY[key as EventCatalogProductKey];
}

/** Distinct `displayFeatureFlag` values across catalog entries (for batched access checks). */
export function getCatalogDisplayFeatureFlagNames(): string[] {
  const names = new Set<string>();
  const entries = EVENT_CATALOG_ENTRIES as readonly EventCatalogEntry[];
  for (const e of entries) {
    if (e.displayFeatureFlag) names.add(e.displayFeatureFlag);
  }
  return [...names];
}

export function catalogUsesDisplayFeatureFlags(): boolean {
  const entries = EVENT_CATALOG_ENTRIES as readonly EventCatalogEntry[];
  return entries.some((e) => Boolean(e.displayFeatureFlag));
}

function filterCatalogKeysByFeatureAccess(
  keys: EventCatalogProductKey[],
  featureAccess: Record<string, boolean> | undefined,
): EventCatalogProductKey[] {
  if (!featureAccess) return keys;
  return keys.filter((key) => {
    const flag = PRODUCT_CATALOG_ENTRY_BY_KEY[key]?.displayFeatureFlag;
    if (!flag) return true;
    return featureAccess[flag] === true;
  });
}

/** Keys aligned with backend `products.quotaConfig` / entitlement defaults for event SKUs. */
export const CATALOG_FALLBACK_QUOTA_KEY_BY_PRODUCT_KEY: Partial<Record<string, string>> = Object.fromEntries(
  EVENT_CATALOG_ENTRIES.map((e) => [e.key, e.fallbackQuotaKey]),
);

const EVENT_SCOPE_LC = PRODUCT_SCOPE_TYPES.EVENT.toLowerCase();

export function filterOrderedEventCatalogKeysForStore(products: ProductDto[]): EventCatalogProductKey[] {
  if (!products.length) return [];
  const apiKeys = new Set(
    products.filter((p) => String(p.scopeType).toLowerCase() === EVENT_SCOPE_LC).map((p) => String(p.key)),
  );
  return EVENT_CATALOG_ENTRIES.filter((e) => apiKeys.has(e.key)).map((e) => e.key);
}

function filterCatalogKeysByEntryVisibility(keys: EventCatalogProductKey[]): EventCatalogProductKey[] {
  return keys.filter((key) => PRODUCT_CATALOG_ENTRY_BY_KEY[key]?.visible !== false);
}

export type CatalogUiFilterOptions = {
  /** Map from `displayFeatureFlag` name to access; omit or leave partial to hide gated SKUs. */
  featureAccess?: Record<string, boolean>;
};

/**
 * Ordered catalog keys for UI: full list while products are not loaded yet; once loaded, only SKUs present for Event scope.
 * When `featureAccess` is provided, entries with `displayFeatureFlag` are kept only if that flag is true.
 */
export function getOrderedEventCatalogKeysForUi(
  products: ProductDto[] | undefined,
  options?: CatalogUiFilterOptions,
): EventCatalogProductKey[] {
  const rawBase = products === undefined ? [...EVENT_PRODUCT_KEYS] : filterOrderedEventCatalogKeysForStore(products);
  const baseKeys = filterCatalogKeysByEntryVisibility(rawBase);
  return filterCatalogKeysByFeatureAccess(baseKeys, options?.featureAccess);
}

/**
 * Event-scoped `products` rows that pass `getOrderedEventCatalogKeysForUi`, in the same order as
 * `EVENT_CATALOG_ENTRIES` (after visibility and feature-flag filtering).
 */
export function filterProductsForEventCatalogUi(
  products: ProductDto[] | undefined,
  options?: CatalogUiFilterOptions,
): ProductDto[] {
  if (!products?.length) return [];
  const orderedKeys = getOrderedEventCatalogKeysForUi(products, options);
  const byKey = new Map<string, ProductDto>();
  for (const p of products) {
    if (String(p.scopeType).toLowerCase() !== EVENT_SCOPE_LC) continue;
    const k = String(p.key);
    if (!byKey.has(k)) byKey.set(k, p);
  }
  return orderedKeys.map((key) => byKey.get(key)).filter((p): p is ProductDto => p != null);
}

export function productUsesContributorSnapshotUnitLabel(key: string | undefined): boolean {
  return getProductCatalogEntry(key)?.snapshotCountUsesContributorUnitLabel ?? false;
}

export function countEventCatalogProductsInStore(
  products: ProductDto[] | undefined,
  options?: CatalogUiFilterOptions,
): number {
  return getOrderedEventCatalogKeysForUi(products, options).length;
}
